// File: src/components/EventClassEditor.js
// Classes (luokat) run at an event.
//
// A class optionally points at a series. Choosing one creates the round in that
// series that the class's results go into, because results cannot be imported
// into a round that does not exist. An unraced round is inert in the standings:
// it counts for nobody until it has results in it.
//
// A class can be edited after the fact: organizers routinely create the classes
// first and attach them to a championship once the season is confirmed. What
// closes that door is results, not the round - `has_results`, never `race_id`.

import React, { useState } from 'react';
import { Plus, Trash2, Link2, Edit, Check, X, Users, CopyPlus } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { apiPost, apiPut, apiDelete } from '../services/api';

const EMPTY_FORM = { name: '', series_id: '', series_class_id: '', entry_limit: '' };

// The server explains refusals (results linked, entrants registered) in
// `message`; `error` is only the short code.
const describe = (err, fallback) => (err.body && err.body.message) || err.message || fallback;

function EventClassEditor({ event, series, token, onChange }) {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyClassId, setBusyClassId] = useState(null);
  const [notice, setNotice] = useState('');

  const classes = event.classes || [];

  const addClass = async () => {
    if (!form.name.trim()) {
      setError(t('eventClassNameRequired'));
      return;
    }

    setSaving(true);
    setError('');
    try {
      await apiPost(`/events/${event.id}/classes`, {
        name: form.name.trim(),
        series_class_id: form.series_class_id ? parseInt(form.series_class_id, 10) : null,
        entry_limit: form.entry_limit ? parseInt(form.entry_limit, 10) : null,
        sort_order: classes.length
      }, { token });

      setForm(EMPTY_FORM);
      onChange();
    } catch (err) {
      setError(describe(err, t('failedToSaveClass')));
    } finally {
      setSaving(false);
    }
  };

  // Every class of a championship, in one press. An event running a full round
  // of a four-class series had to fill the same two selects four times.
  //
  // The server decides what to skip and does the lot in one transaction, so
  // pressing this twice - or after adding one class by hand - adds what is
  // missing rather than refusing everything.
  const addAllFromSeries = async () => {
    if (!form.series_id) {
      setError(t('eventPickSeriesFirst'));
      return;
    }

    setSaving(true);
    setError('');
    setNotice('');
    try {
      const result = await apiPost(`/events/${event.id}/classes/from-series`,
        { series_id: parseInt(form.series_id, 10) }, { token });

      // Nothing added and nothing to add is worth saying plainly - an empty
      // "0 classes added" reads as a failure when it is the opposite.
      if (result.created === 0 && result.skipped.length > 0) {
        setNotice(t('eventAddAllFromSeriesNone'));
      } else {
        const added = t('eventAddAllFromSeriesDone').replace('{created}', result.created);
        setNotice(result.skipped.length > 0
          ? `${added} • ${t('eventAddAllFromSeriesSkipped').replace('{skipped}', result.skipped.length)}`
          : added);
      }

      setForm(EMPTY_FORM);
      onChange();
    } catch (err) {
      setError(describe(err, t('failedToSaveClass')));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (eventClass) => {
    setEditingId(eventClass.id);
    setEditForm({
      name: eventClass.name,
      series_id: eventClass.series_id ? String(eventClass.series_id) : '',
      series_class_id: eventClass.series_class_id ? String(eventClass.series_class_id) : '',
      entry_limit: eventClass.entry_limit ? String(eventClass.entry_limit) : ''
    });
    setError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
    setError('');
  };

  const saveEdit = async (eventClass) => {
    if (!editForm.name.trim()) {
      setError(t('eventClassNameRequired'));
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        name: editForm.name.trim(),
        entry_limit: editForm.entry_limit ? parseInt(editForm.entry_limit, 10) : null
      };

      // Only send series_id when it may actually change. Once results are
      // recorded the server refuses the change, and sending the unchanged value
      // would trip that guard for no reason.
      if (!eventClass.has_results) {
        payload.series_class_id = editForm.series_class_id
          ? parseInt(editForm.series_class_id, 10) : null;
      }

      await apiPut(`/event-classes/${eventClass.id}`, payload, { token });
      cancelEdit();
      onChange();
    } catch (err) {
      setError(describe(err, t('failedToSaveClass')));
    } finally {
      setSaving(false);
    }
  };

  const removeClass = async (eventClass) => {
    if (!window.confirm(t('deleteClassConfirm'))) return;

    try {
      await apiDelete(`/event-classes/${eventClass.id}`, { token });
      onChange();
    } catch (err) {
      // The server refuses when entrants or results depend on the class, and
      // explains why - surface that rather than a generic failure.
      setError(describe(err, t('failedToDeleteClass')));
    }
  };

  // Creates the series round this class's results go into. Choosing a series
  // does this by itself now, so this covers the classes that chose theirs
  // before that was true - and anything an admin has since unlinked.
  const linkResults = async (eventClass) => {
    setBusyClassId(eventClass.id);
    setError('');
    setNotice('');
    try {
      await apiPost(`/event-classes/${eventClass.id}/link-race`, {}, { token });
      setNotice(t('linkResultsDone'));
      onChange();
    } catch (err) {
      setError((err.body && err.body.message) || err.message || t('linkFailed'));
    } finally {
      setBusyClassId(null);
    }
  };

  const syncDrivers = async (eventClass) => {
    setBusyClassId(eventClass.id);
    setError('');
    setNotice('');
    try {
      const result = await apiPost(`/event-classes/${eventClass.id}/sync-drivers`, {}, { token });
      setNotice(
        t('syncDriversResult')
          .replace('{created}', result.created)
          .replace('{matched}', result.matched)
          .replace('{already}', result.alreadyLinked)
      );
      // A shared car number is the organizer's to resolve, so name the people.
      if (result.conflicts.length > 0) {
        setError(`${t('syncDriversConflicts')}: ` + result.conflicts
          .map(c => `${c.name} (#${c.car_number}) - ${t('syncDriversConflictNumber')}`)
          .join('; '));
      }
      onChange();
    } catch (err) {
      setError((err.body && err.body.message) || err.message || t('linkFailed'));
    } finally {
      setBusyClassId(null);
    }
  };

  const classesOf = (seriesId) =>
    (series.find(s => String(s.id) === String(seriesId)) || {}).classes || [];

  // Choosing a series clears the class under it, then picks it straight back
  // when there is only one to pick. Every championship that predates classes
  // has exactly one, so for those this is the single decision it always was.
  const pickSeries = (value, current, setter) => {
    const only = classesOf(value);
    const next = {
      ...current,
      series_id: value,
      series_class_id: only.length === 1 ? String(only[0].id) : ''
    };
    if (only.length === 1) applyClassName(next, only[0]);
    setter(next);
  };

  // Naming the event class after the championship class is the common case -
  // an event running "M4-10 Stock" calls its class that. It used to copy the
  // series name, which was the same string only because a series was a class.
  const applyClassName = (draft, picked) => {
    if (picked && !draft.name.trim()) draft.name = picked.name;
  };

  const pickClass = (value, current, setter) => {
    const next = { ...current, series_class_id: value };
    applyClassName(next, classesOf(current.series_id)
      .find(c => String(c.id) === String(value)));
    setter(next);
  };

  const selectBox = (value, onSelect, options, placeholder, { disabled = false } = {}) => (
    <select
      value={value}
      onChange={(e) => onSelect(e.target.value)}
      disabled={disabled}
      className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink disabled:opacity-60"
    >
      <option value="">{placeholder}</option>
      {options.map(o => (
        <option key={o.id} value={o.id}>{o.name}</option>
      ))}
    </select>
  );

  // Two levels, because a championship is a series AND a class within it. The
  // class select stays put but empty until a series is chosen, so the row does
  // not jump around as you fill it in.
  const championshipSelects = (form, setter, { disabled = false } = {}) => (
    <>
      {selectBox(form.series_id, (v) => pickSeries(v, form, setter),
        series, t('eventClassNoSeries'), { disabled })}
      {selectBox(form.series_class_id, (v) => pickClass(v, form, setter),
        classesOf(form.series_id), t('seriesClass'),
        { disabled: disabled || !form.series_id })}
    </>
  );

  return (
    <div>
      <label className="block text-ink-muted mb-2 font-semibold">{t('eventClasses')}</label>

      {classes.length === 0 ? (
        <p className="text-ink-muted text-sm mb-3">{t('eventNoClasses')}</p>
      ) : (
        <div className="space-y-2 mb-3">
          {classes.map(eventClass => (
            editingId === eventClass.id ? (
              <div key={eventClass.id} className="bg-paper-alt px-3 py-3 rounded space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2">
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    placeholder={t('eventClassName')}
                    className="w-full px-4 py-2 bg-paper border border-line rounded text-ink"
                  />
                  {championshipSelects(editForm, setEditForm,
                    { disabled: Boolean(eventClass.has_results) })}
                  <input
                    type="number"
                    min="1"
                    value={editForm.entry_limit}
                    onChange={(e) => setEditForm({ ...editForm, entry_limit: e.target.value })}
                    placeholder={t('eventClassEntryLimit')}
                    className="w-full md:w-32 px-4 py-2 bg-paper border border-line rounded text-ink"
                  />
                </div>

                {/* Changing the series after results exist would leave the race
                    pointing at drivers from the old series. An empty round moves
                    with the class, so only recorded results lock this. */}
                {Boolean(eventClass.has_results) && (
                  <p className="text-ink-muted text-sm">{t('eventClassSeriesLocked')}</p>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => saveEdit(eventClass)}
                    disabled={saving}
                    className="bg-accent hover:opacity-90 disabled:opacity-50 text-accent-ink px-4 py-2 rounded font-semibold flex items-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    {t('save')}
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="bg-paper hover:opacity-80 text-ink px-4 py-2 rounded font-semibold flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    {t('cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div key={eventClass.id} className="flex items-center gap-3 bg-paper-alt px-3 py-2 rounded">
                <div className="flex-1 min-w-0">
                  <div className="text-ink font-medium truncate">{eventClass.name}</div>
                  <div className="text-ink-muted text-sm truncate">
                    {eventClass.series_class_name
                      ? `${eventClass.series_name} — ${eventClass.series_class_name}`
                      : t('eventClassNoSeries')}
                    {eventClass.entry_limit ? ` • ${t('eventClassEntryLimit')}: ${eventClass.entry_limit}` : ''}
                  </div>
                </div>

                {/* Three states, and the middle one is the new normal: a round
                    waiting for the results of a race that has not run yet. It
                    reads differently from results that are actually in, because
                    only the latter freezes the class's series. */}
                {eventClass.race_id ? (
                  <>
                    <span
                      className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded whitespace-nowrap ${
                        eventClass.has_results
                          ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400'
                          : 'bg-paper text-ink-muted border border-line'
                      }`}
                      title={eventClass.has_results
                        ? t('eventClassLinkedResults')
                        : t('eventClassRoundReadyHelp')}
                    >
                      <Link2 className="w-3 h-3" />
                      {eventClass.has_results
                        ? t('eventClassLinkedResults')
                        : t('eventClassRoundReady')}
                    </span>
                    <button
                      onClick={() => syncDrivers(eventClass)}
                      disabled={busyClassId === eventClass.id}
                      className="text-ink-muted hover:text-ink disabled:opacity-50"
                      title={t('syncDrivers')}
                    >
                      <Users className="w-4 h-4" />
                    </button>
                  </>
                ) : eventClass.series_id ? (
                  <button
                    onClick={() => linkResults(eventClass)}
                    disabled={busyClassId === eventClass.id}
                    className="text-xs font-semibold px-2 py-1 rounded bg-paper hover:opacity-80 text-ink-muted border border-line disabled:opacity-50 whitespace-nowrap"
                    title={t('linkResultsHelp')}
                  >
                    {t('linkResults')}
                  </button>
                ) : null}

                <button
                  onClick={() => startEdit(eventClass)}
                  className="text-blue-700 dark:text-blue-400 hover:opacity-80"
                  title={t('edit')}
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removeClass(eventClass)}
                  className="text-red-700 dark:text-red-400 hover:opacity-80"
                  title={t('delete')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )
          ))}
        </div>
      )}

      {/* Adding is hidden while editing, so there is only ever one form open. */}
      {editingId === null && (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto_auto_auto] gap-2">
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={t('eventClassName')}
            className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
          />
          {championshipSelects(form, setForm)}
          <input
            type="number"
            min="1"
            value={form.entry_limit}
            onChange={(e) => setForm({ ...form, entry_limit: e.target.value })}
            placeholder={t('eventClassEntryLimit')}
            className="w-full md:w-32 px-4 py-2 bg-paper-alt border border-line rounded text-ink"
          />
          <button
            onClick={addClass}
            disabled={saving}
            className="bg-paper-alt hover:opacity-80 disabled:opacity-50 text-ink px-4 py-2 rounded font-semibold flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t('eventAddClass')}
          </button>
          {/* Enabled by the series select above it, and it ignores the name
              field entirely - each class is named after the one it copies. */}
          <button
            onClick={addAllFromSeries}
            disabled={saving || !form.series_id}
            title={t('eventAddAllFromSeriesHelp')}
            className="bg-paper-alt hover:opacity-80 disabled:opacity-50 text-ink px-4 py-2 rounded font-semibold flex items-center justify-center gap-2 whitespace-nowrap"
          >
            <CopyPlus className="w-4 h-4" />
            {t('eventAddAllFromSeries')}
          </button>
        </div>
      )}

      {notice && (
        <div className="mt-3 bg-green-50 dark:bg-green-500/20 border border-green-500 text-green-800 dark:text-green-200 px-4 py-3 rounded text-sm">
          {notice}
        </div>
      )}

      {error && (
        <div className="mt-3 bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}
    </div>
  );
}

export default EventClassEditor;
